import { Fragment, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { InfiniteScroll, ListRow, NoResults } from 'basic-components';
import { ArrowLeft, Exit } from 'basic-components/icons';
import Query from 'dashboard-services/api/Query';
import { StatusesEnums, StatusesQuery } from 'dashboard-services/api/statuses';
import { CommonService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { Fetching, globalActions, StatusIcon, StatusStyleService } from 'primary-components';

import { statusesActions } from 'actions/modules/statuses';
import getApiConfig from 'api/ApiConfig';

import RunTime from 'basic/RunTime';

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { When } from 'react-if';

import './HistoryMobile.scss';

const REQUEST_ID = "History_Mobile_Statuses"
const useSearchStatuses = ({ name, groupName, type, getQueryBuilder, shouldBeLimited }) =>
  useInfiniteQuery({
    ...StatusesQuery.QUERIES.search(
      getQueryBuilder, 
      { 
        query: name || "*",
        groupName, 
        size: 20, 
        type, 
        REQUEST_ID
      }
    ),
    initialPageParam: 0,
    refetchInterval: () => shouldBeLimited() ? undefined : 5000,
    getNextPageParam: CommonService.getNextPageParam
  })
const HistoryMobile = () => {
  const dispatch = useDispatch(),
        navigate = useNavigate(),
        queryClient = useQueryClient(),
        { groupName, name, type } = useParams(),
        definedTime = useSelector(state => state.myAccountState.settingsState.definedTime),
        stateJobId = useSelector(state => state.statusesState.jobId),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        { data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useSearchStatuses({ name, getQueryBuilder, shouldBeLimited }),
        scrollCont = useRef(),
        onScroll = useCallback(() => fetchNextPage(), [fetchNextPage]),
        navigateFromHistory = useCallback(({ jobId, toDetails }) => {
          const targetJobId = jobId || stateJobId
          if(jobId && toDetails){
            dispatch(statusesActions.changeValue(jobId, "jobId"))
          }
          navigate(DashboardLinkFactory.statuses.details({ baseUrl: "", fromHistory: targetJobId ? toDetails : undefined, groupName: targetJobId ? groupName : undefined, jobId: targetJobId, name: targetJobId && name ? name : undefined, type }))
        }, [dispatch, groupName, name, navigate, stateJobId, type])

  useEffect(() => () => {
    queryClient.cancelQueries({ queryKey: [StatusesQuery.KEYS.SEARCH, REQUEST_ID] })
  }, [queryClient])

  return (
    <Fragment>
      <When condition={data?.pages?.length === 1 && !isPending && data?.pages?.[0]?.items.length === 0}>
        {() => (
          <NoResults/>
        )}
      </When>
      <When condition={isFetchingNextPage || isPending}>
        <Fetching/>
      </When>
      <div className="statuses-history-view-mobile">
        <div className="statuses-history-view-mobile__header">
          <div 
              className="statuses-history-view-mobile__header__button"
              onClick={() => navigateFromHistory({ toDetails: false })}
          >
            <ArrowLeft
                color="blue-bright"
                height={32}
                width={32}
            />
          </div>
          <div className="statuses-history-view-mobile__header__details">
            <div className="statuses-history-view-mobile__header__details__title">
              Last Runs
            </div>
            <div className="statuses-history-view-mobile__header__details__name">
              {name || groupName}
            </div>
          </div>
        </div>
        <div className="statuses-history-view-mobile__container">
          <InfiniteScroll
              className="statuses-history-view-mobile__container__infinite-scroll"
              hasMore={hasNextPage}
              initialLoad={false}
              loadMore={onScroll}
              useWindow={false}
          >
            {(data?.pages || []).map((page, pageIndex) => (
              <Fragment key={pageIndex}>
                {page.items.map((status, index) => (
                  <ListRow
                      className="statuses-history-view-mobile__container__element"
                      height={72}
                      key={status.jobId + index}
                      scrollContRef={scrollCont}
                  >
                    <div 
                        className="statuses-history-view-mobile__container__element__container" 
                        style={{ borderLeftColor: StatusStyleService.getTagLabelStatusStyle(status.status).color }}
                    >
                      <div className="statuses-history-view-mobile__container__element__container__icon">
                        <StatusIcon
                            status={status?.latestStatus?.status || status?.status}
                        />
                      </div>
                      <div className="statuses-history-view-mobile__container__element__container__details">
                        <div className="statuses-history-view-mobile__container__element__container__details__time">
                          <div className="statuses-history-view-mobile__container__element__container__details__time__date">
                            {String(TimeService.getUserFormattedDateTime(status?.startTime, definedTime).split(" ")[0])}
                          </div>
                          <div className="statuses-history-view-mobile__container__element__container__details__time__hours">
                            {String(TimeService.getUserFormattedDateTime(status?.startTime, definedTime).split(" ")[1])}
                          </div>
                        </div>
                        <div className="statuses-history-view-mobile__container__element__container__details__duration">
                          Duration: {(
                            <RunTime
                                endTime={status?.endTime}
                                isRunning={status?.status === StatusesEnums.STATUS.RUNNING || status?.status === StatusesEnums.STATUS.PAUSED}
                                startTime={status?.startTime}
                            />
                          )}
                        </div>
                      </div>
                      <div 
                          className="statuses-history-view-mobile__container__element__container__link"
                          onClick={() => navigateFromHistory({ 
                            jobId: status?.jobId,
                            toDetails: true
                          })}
                      >
                        <Exit
                            color="blue-bright"
                            height={24}
                            width={24}
                        />
                    </div>
                    </div>
                  </ListRow>
                ))}
              </Fragment>
            ))}
          </InfiniteScroll>
        </div>
      </div>
    </Fragment>
  )
}

export default HistoryMobile;
